/**
 * No need to import individual stores, already handled by `unplugin-auto-import/vite`
 * See `vite.config.js`
 **/

import { useCompanyFilterStore } from '@/stores/data-tables/companyFilter';
import { useCustomerFilterStore } from '@/stores/data-tables/customerFilter';
import { useInvoiceFilterStore } from '@/stores/data-tables/invoiceFilter';
import { useInvoiceReminderFilterStore } from '@/stores/data-tables/invoiceReminderFilter';
import { useProductFilterStore } from '@/stores/data-tables/productFilter';
import { useReceiptFilterStore } from '@/stores/data-tables/receiptFilter';
import { useRoleFilterStore } from '@/stores/data-tables/roleFilter';
import { useTaxFilterStore } from '@/stores/data-tables/taxFilter';
import { useTaxTypeFilterStore } from '@/stores/data-tables/taxTypeFilter';
import { useUserFilterStore } from '@/stores/data-tables/userFilter';

import { getActivePinia } from 'pinia';

const filterStoresRegistry = {
  users: useUserFilterStore,
  roles: useRoleFilterStore,
  companies: useCompanyFilterStore,
  customers: useCustomerFilterStore,
  products: useProductFilterStore,
  'tax-types': useTaxTypeFilterStore,
  taxes: useTaxFilterStore,
  invoices: useInvoiceFilterStore,
  'invoice-reminders': useInvoiceReminderFilterStore,
  receipts: useReceiptFilterStore,
};

// Resouce name is plural in Laravel
export function getFilterStore(resouceName) {
  const store = filterStoresRegistry[resouceName];
  if (!store) {
    throw new Error(`Store "${resouceName}" not registered`);
  }
  return store();
}

// --- Reset ALL, not only for TableSettings stores ---
export function resetAllStores() {
  const pinia = getActivePinia();
  pinia._s.forEach((store) => store.$reset());
}

// --- Data Table Filter store factory ---
export function createDataTableFilterStore(
  storeId,
  defaultFilters,
  defaultSortKey = 'created_at',
  defaultSortOrder = 'desc'
) {
  const store = defineStore(storeId, {
    state: () => ({
      filters: { ...defaultFilters },
      sortBy: {
        key: defaultSortKey,
        order: defaultSortOrder,
      },
    }),

    getters: {
      // Can not use arrow function, because `this` will be referenced by `queryParams()`
      sortKey(state) {
        return state.sortBy.key;
      },

      sortOrder(state) {
        return state.sortBy.order;
      },

      filtersCopy(state) {
        return { ...state.filters };
      },

      defaultFiltersCopy() {
        return { ...defaultFilters };
      },

      defaultSortKey() {
        return defaultSortKey;
      },

      defaultSortOrder() {
        return defaultSortOrder;
      },

      // Only add `sort_key`, `sort_order` and `filters` that differs from default
      queryParams(state) {
        const params = {};
        if (this.sortKey !== defaultSortKey) {
          params.sort_key = this.sortKey;
        }

        if (this.sortOrder !== defaultSortOrder) {
          params.sort_order = this.sortOrder;
        }

        Object.entries(state.filters).forEach(([key, value]) => {
          if (value !== defaultFilters[key]) {
            params[key] = value;
          }
        });

        return params;
      },
    },

    actions: {
      reset() {
        this.$reset();
      },

      resetFilters() {
        this.filters = { ...defaultFilters };
      },

      updateFilter(key, value) {
        this.filters[key] = value;
      },

      setFilters(newFilters) {
        this.filters = { ...newFilters };
      },

      setSortBy(key, order) {
        this.sortBy.key = key;
        this.sortBy.order = order;
      },

      setSortKey(key) {
        this.sortBy.key = key;
      },

      setSortOrder(order) {
        this.sortBy.order = order;
      },
    },

    persist: {
      key: `${storeId}_store`,
      storage: localStorage,
    },
  });
  return store;
}
